import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import * as Toast from '@radix-ui/react-toast';
import { ToastRoot, ToastViewport, IconWrapper } from './styles';
import { truncateHtml } from '@/shared/libs/truncate-html';
import { Button } from '@/shared/ui/form/button';
import { Box } from '@/shared/ui/box';
import _uniqueId from 'lodash/uniqueId';
import { useI18n } from '@locales/client';
import { Image } from '@/shared/ui/image';
import { useRouter } from 'next-nprogress-bar';
import {
  BodyTextBoldSecondary,
  BodyTextRegularPrimary,
  BodyTextRegularSecondary,
  BoldHeadingQuinary,
} from '@/shared/ui/typography';

const ToastContext = createContext();

const TOAST_ICONS = {
  system: '/images/icons/toast/system.svg',
  bonus: '/images/icons/toast/bonus.svg',
  error: '/images/icons/toast/warning.svg',
  loyalty: (level) => `/images/icons/toast/status_level_${level}.svg`,
  newLevel: (level) => `/images/icons/toast/status_level_${level}.svg`,
};

const ToastContent = ({ toast, t, hideToast }) => {
  const router = useRouter();

  const handleToastClick = useCallback(() => {
    if (!toast.link) return;

    hideToast(toast.ts);

    if (toast.link.includes('openSupport=true')) {
      window.LiveChatWidget?.call('maximize');
      return;
    }

    router.push(toast.link);
  }, [toast, router, hideToast]);

  const renderIcon = useMemo(() => {
    let iconSrc;

    if (toast.title === 'newLevel') {
      const level = parseInt(toast.text, 10) - 1;
      iconSrc = TOAST_ICONS.newLevel(level);
    } else if (toast.type === 'loyalty') {
      iconSrc = TOAST_ICONS.loyalty(toast.level || 0);
    } else {
      iconSrc = TOAST_ICONS[toast.type] || TOAST_ICONS.error;
    }

    return (
      <IconWrapper $status={toast.status}>
        <Image src={iconSrc} alt={toast.type} width={24} height={24} />
      </IconWrapper>
    );
  }, [toast]);

  const ContentContainer = ({ children }) => (
    <Box
      $display="flex"
      $gap={12}
      $alignItems="center"
      onClick={toast.link ? handleToastClick : undefined}
      role={toast.link ? 'button' : undefined}
    >
      <Box>{renderIcon}</Box>
      <Box>{children}</Box>
    </Box>
  );

  if (toast.title === 'newLevel') {
    return (
      <ContentContainer>
        <>
          <BoldHeadingQuinary color="#FFF">{toast?.title}</BoldHeadingQuinary>
          <BodyTextRegularPrimary color="#FFF">
            {toast?.text}
          </BodyTextRegularPrimary>
        </>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer>
      <>
        <BodyTextBoldSecondary color="#FFF">
          {toast.title}
        </BodyTextBoldSecondary>
        <BodyTextRegularSecondary>
          {truncateHtml(toast.text, 105)}
        </BodyTextRegularSecondary>

        {toast.action && (
          <Box $mt={8}>
            <Button
              variant="primary"
              onClick={() => toast.link && router.push(toast.link)}
              type="button"
            >
              {toast.action}
            </Button>
          </Box>
        )}
      </>
    </ContentContainer>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const t = useI18n();

  const showErrorToast = useCallback(
    (error, args = {}) => {
      const typeForImg = error?.type || 'error';
      const errorCode = error?.error || 'unknown';

      const titleKey = t(`common.api_errors.${errorCode}.title`);
      const descKey = t(`common.api_errors.${errorCode}.desc`);

      const existsKey = !(
        titleKey.startsWith('common') || descKey.startsWith('common')
      );

      if (existsKey) {
        const notify = {
          ts: _uniqueId('toast_'),
          type: 'error',
          status: 'error',
          title: titleKey,
          text: descKey,
          typeForImg,
          key: `error-${errorCode}`,
        };

        setToasts((prev) => {
          const duplicate = prev.some((t) => t.key === notify.key);
          if (duplicate) return prev;
          return [...prev, notify];
        });
      }
    },
    [t]
  );

  const showToast = useCallback(
    (notify) => {
      if (notify.type === 'error' && notify.errorString) {
        return showErrorToast(notify.errorString, notify.args);
      }

      const newToast = {
        ts: _uniqueId('toast_'),
        ...notify,
        key: notify.key || `${notify.title}-${notify.text}`,
      };

      setToasts((prev) => {
        const duplicate = prev.some((t) => t.key === newToast.key);
        if (duplicate) return prev;
        return [...prev, newToast];
      });

      if (Notification.permission === 'granted' && notify.save) {
        new Notification(notify.title, {
          body: notify.text,
          image: notify?.image,
        });
      }
    },
    [showErrorToast]
  );

  const hideToast = useCallback((ts) => {
    setToasts((prev) => prev.filter((toast) => toast.ts !== ts));
  }, []);

  const contextValue = useMemo(
    () => ({
      showToast,
      hideToast,
      showErrorToast,
    }),
    [showToast, hideToast, showErrorToast]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      <Toast.Provider swipeDirection="up">
        {toasts.map((toast) => (
          <ToastRoot
            key={toast.ts}
            duration={8000}
            $hasAction={!!toast.action}
            $status={toast.status}
            $type={toast.type}
            onOpenChange={() => hideToast(toast.ts)}
          >
            <ToastContent toast={toast} t={t} hideToast={hideToast} />
          </ToastRoot>
        ))}
        <ToastViewport />
      </Toast.Provider>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};